import React from "react";
import { Navigate, Outlet, Route, useParams } from "react-router-dom";

import UI from "@/features/ui";
import {
  AccountGallery,
  AccountSearch,
  AccountTimeline,
  AdsSettings,
  AiSearch,
  AiSearchResults,
  AlertAccounts,
  Alerts,
  AlertSettings,
  BlocksSettings,
  Bookmarks,
  BoostDashboard,
  ChatIndex,
  DeleteAccountSettings,
  Discover,
  DiscoverabilitySettings,
  DownloadYourInformationSettings,
  EditFeed,
  FavouritedStatuses,
  Feeds,
  GameDetails,
  GemsLeaderboard,
  GenericNotFound,
  GroupAbout,
  GroupEvents,
  GroupGallery,
  GroupSearch,
  GroupTagTimeline,
  GroupTags,
  GroupTimeline,
  Groups,
  GroupsCategory,
  GroupsDiscover,
  GroupsSuggested,
  GroupsTag,
  HashtagTimeline,
  HomeTimeline,
  LogoutPage,
  MessagesSettings,
  MfaSettings,
  MuteSettings,
  NewsArticleDetail,
  NewStatus,
  OtherSettings,
  PasswordSettings,
  PendingGroupRequests,
  PredictionDetail,
  Predictions,
  PreferencesSettings,
  PrivacySettings,
  ProfileSettings,
  Search,
  SecuritySettings,
  ServerInfo,
  SessionsSettings,
  Settings,
  Share,
  SportsLive,
  SportsNews,
  SportsUpcoming,
  Status,
  StatusHistory,
  SubscriptionPayments,
  SubscriptionSettings,
  TeamDetails,
} from "@/features/ui/util/async-components";
import { WrappedRoute } from "@/features/ui/util/react-router-helpers";
import { useAccountRouteParams } from "@/hooks/useAccountRouteParams";
import AiPage from "@/pages/ai-page";
import ChatsPage from "@/pages/chats-page";
import DefaultPage from "@/pages/default-page";
import EmptyPage from "@/pages/empty-page";
import FullPage from "@/pages/full-page";
import GroupPage from "@/pages/group-page";
import GroupSearchPage from "@/pages/group-search-page";
import GroupsPage from "@/pages/groups-page";
import GroupsPendingPage from "@/pages/groups-pending-page";
import HomePage from "@/pages/home-page";
import PredictionPage from "@/pages/prediction-page";
import PredictionsPage from "@/pages/predictions-page";
import ProfilePage from "@/pages/profile-page";
import SearchPage from "@/pages/search-page";
import SettingsPage from "@/pages/settings-page";
import StatusPage from "@/pages/status-page";

import GroupGuard from "./guards/group-guard";

import type { Features } from "@/utils/features";

// AccountTimeline is mounted on both the posts and replies routes; the replies
// route passes withReplies explicitly rather than the component inferring it
// from the pathname.
const AccountRepliesTimeline = () => <AccountTimeline withReplies />;

function WebRedirect() {
  const { path1, path2, path3, path } = useParams();

  let to = "/";

  if (path3 && path2 && path1) {
    to = `/${path1}/${path2}/${path3}`;
  } else if (path2 && path1) {
    to = `/${path1}/${path2}`;
  } else if (path1) {
    to = `/${path1}`;
  } else if (path) {
    to = `/${path}`;
  }

  return <Navigate to={to} replace />;
}

function LegacyGroupStatusRedirect() {
  const { groupSlug, statusId } = useParams<{
    groupSlug: string;
    statusId: string;
  }>();
  if (!groupSlug || !statusId) return null;
  return <Navigate to={`/group/${groupSlug}/posts/${statusId}`} replace />;
}

function LegacyUserStatusRedirect() {
  const { username, statusId } = useParams<{
    username: string;
    statusId: string;
  }>();
  if (!username || !statusId) return null;
  return <Navigate to={`/@${username}/posts/${statusId}`} replace />;
}

function LegacyAccountStatusRedirect() {
  const { username, statusId } = useAccountRouteParams();

  if (!username || !statusId) return null;
  // useAccountRouteParams strips the leading "@", so re-add it for the target.
  return <Navigate to={`/@${username}/posts/${statusId}`} replace />;
}

function EmbedRedirect() {
  const { statusId } = useParams<{ statusId: string }>();
  if (!statusId) return null;
  return <Navigate to={`/embed/${statusId}`} replace />;
}

/**
 * React Router v6 does not support the `:param` syntax after a non-`/` literal
 * (i.e. `/@:username` is matched as a literal string, not as a param).
 *
 * Workaround: use `path="/:username"` and require URL to start with `@` here.
 * The leading `@` is stripped before reaching leaf components by `WrappedRoute`.
 */
const AccountLayoutRoute = () => {
  const { username } = useParams<{ username: string }>();
  if (!username?.startsWith("@")) {
    return <WrappedRoute page={EmptyPage} component={GenericNotFound} />;
  }
  return <Outlet />;
};

interface AppRoutesProps {
  features: Features;
  search: string;
  authenticatedProfile: boolean;
}

export const renderAppRoutes = ({
  features,
  search,
  authenticatedProfile,
}: AppRoutesProps) => (
  <Route element={<UI />}>
    <Route
      path="/logout"
      element={
        <WrappedRoute page={EmptyPage} component={LogoutPage} publicRoute />
      }
    />
    <Route
      path="/"
      element={<WrappedRoute page={HomePage} component={HomeTimeline} />}
    />

    {/* Mastodon web routes */}
    <Route path="/web/:path1/:path2/:path3" element={<WebRedirect />} />
    <Route path="/web/:path1/:path2" element={<WebRedirect />} />
    <Route path="/web/:path" element={<WebRedirect />} />
    <Route path="/timelines/home" element={<Navigate to="/" replace />} />
    <Route
      path="/timelines/public/local"
      element={<Navigate to="/timeline/local" replace />}
    />
    <Route
      path="/timelines/public"
      element={<Navigate to="/timeline/fediverse" replace />}
    />
    <Route
      path="/timelines/direct"
      element={<Navigate to="/messages" replace />}
    />

    {/* Mastodon rendered pages */}
    <Route
      path="/settings/two_factor_authentication_methods"
      element={<Navigate to="/settings/mfa" replace />}
    />
    <Route
      path="/settings/otp_authentication"
      element={<Navigate to="/settings/mfa" replace />}
    />
    <Route path="/auth/edit" element={<Navigate to="/settings" replace />} />
    <Route
      path="/auth/confirmation"
      element={<Navigate to={`/email-confirmation${search}`} replace />}
    />
    <Route
      path="/auth/reset_password"
      element={<Navigate to="/reset-password" replace />}
    />
    <Route
      path="/auth/edit_password"
      element={<Navigate to="/edit-password" replace />}
    />
    <Route path="/auth/sign_in" element={<Navigate to="/login" replace />} />
    <Route path="/auth/sign_out" element={<Navigate to="/logout" replace />} />

    {/* Soapbox Legacy redirects */}
    <Route
      path="/auth/external"
      element={<Navigate to="/login/external" replace />}
    />
    <Route path="/auth/mfa" element={<Navigate to="/settings/mfa" replace />} />
    <Route
      path="/auth/password/new"
      element={<Navigate to="/reset-password" replace />}
    />
    <Route
      path="/auth/password/edit"
      element={<Navigate to={`/edit-password${search}`} replace />}
    />
    <Route
      path="/tv-auth/password/edit"
      element={<Navigate to={`/edit-password${search}`} replace />}
    />
    <Route
      path="/blocks"
      element={<Navigate to="/settings/blocks" replace />}
    />
    <Route path="/mutes" element={<Navigate to="/settings/mutes" replace />} />
    <Route
      path="/settings/tokens"
      element={<Navigate to="/settings/sessions" replace />}
    />
    <Route
      path="/settings/email"
      element={<Navigate to="/settings/security" replace />}
    />
    <Route path="/notifications" element={<Navigate to="/alerts" replace />} />

    <Route
      path="/patriot-onboarding"
      element={<Navigate to="/?patriot-onboarding=1" replace />}
    />

    <Route
      path="/tags/:id"
      element={
        <WrappedRoute
          publicRoute
          page={DefaultPage}
          component={HashtagTimeline}
        />
      }
    />

    <Route
      path="/alerts"
      element={<WrappedRoute page={DefaultPage} component={Alerts} />}
    />
    <Route
      path="/alerts/:id/accounts"
      element={<WrappedRoute page={DefaultPage} component={AlertAccounts} />}
    />
    <Route
      path="/alerts/settings"
      element={<WrappedRoute page={DefaultPage} component={AlertSettings} />}
    />

    <Route
      path="/discover"
      element={<WrappedRoute page={SearchPage} component={Discover} />}
    />

    {features.sports && (
      <>
        <Route
          path="/sports/live"
          element={<WrappedRoute page={SearchPage} component={SportsLive} />}
        />
        <Route
          path="/sports/upcoming"
          element={
            <WrappedRoute page={SearchPage} component={SportsUpcoming} />
          }
        />
        <Route
          path="/sports/:sportType/games/:gameId"
          element={<WrappedRoute page={SearchPage} component={GameDetails} />}
        />
        <Route
          path="/sports/:sportType/teams/:teamId"
          element={<WrappedRoute page={SearchPage} component={TeamDetails} />}
        />
        <Route
          path="/sports/news"
          element={<WrappedRoute page={SearchPage} component={SportsNews} />}
        />
        <Route
          path="/sports/news/:articleId"
          element={
            <WrappedRoute page={SearchPage} component={NewsArticleDetail} />
          }
        />
      </>
    )}

    <Route
      path="/search"
      element={<WrappedRoute page={SearchPage} component={Search} />}
    />
    <Route
      path="/ai-search"
      element={<WrappedRoute page={AiPage} component={AiSearch} />}
    />
    <Route
      path="/ai-search/results"
      element={<WrappedRoute page={AiPage} component={AiSearchResults} />}
    />

    <Route
      path="/chats"
      element={<WrappedRoute page={ChatsPage} component={ChatIndex} />}
    />
    <Route
      path="/chats/settings"
      element={<WrappedRoute page={ChatsPage} component={ChatIndex} />}
    />
    <Route
      path="/chats/:chatId"
      element={<WrappedRoute page={ChatsPage} component={ChatIndex} />}
    />

    <Route
      path="/gems/leaderboard"
      element={<WrappedRoute page={DefaultPage} component={GemsLeaderboard} />}
    />

    {/* Account routes — see AccountLayoutRoute comment for v6 limitation */}
    <Route path="/:username" element={<AccountLayoutRoute />}>
      <Route
        index
        element={
          <WrappedRoute
            publicRoute
            page={ProfilePage}
            component={AccountTimeline}
          />
        }
      />
      <Route
        path="with_replies"
        element={
          <WrappedRoute
            publicRoute={!authenticatedProfile}
            page={ProfilePage}
            component={AccountRepliesTimeline}
          />
        }
      />
      <Route
        path="media"
        element={
          <WrappedRoute
            publicRoute={!authenticatedProfile}
            page={ProfilePage}
            component={AccountGallery}
          />
        }
      />
      <Route
        path="tagged/:tag"
        element={
          <WrappedRoute page={ProfilePage} component={AccountTimeline} />
        }
      />
      <Route
        path="favorites"
        element={
          <WrappedRoute page={ProfilePage} component={FavouritedStatuses} />
        }
      />
      <Route
        path="search"
        element={<WrappedRoute page={DefaultPage} component={AccountSearch} />}
      />
      <Route path="posts/:statusId/embed" element={<EmbedRedirect />} />
      <Route
        path="posts/:statusId/history"
        element={<WrappedRoute page={StatusPage} component={StatusHistory} />}
      />
      <Route
        path="posts/:statusId"
        element={
          <WrappedRoute publicRoute page={StatusPage} component={Status} />
        }
      />
      <Route path=":statusId/embed" element={<EmbedRedirect />} />
      <Route path=":statusId" element={<LegacyAccountStatusRedirect />} />
    </Route>

    <Route
      path="/feeds"
      element={<WrappedRoute page={DefaultPage} component={Feeds} />}
    />
    <Route
      path="/feeds/:id/edit"
      element={<WrappedRoute page={DefaultPage} component={EditFeed} />}
    />

    <Route
      path="/groups"
      element={<WrappedRoute page={GroupsPage} component={Groups} />}
    />
    <Route
      path="/groups/discover"
      element={<WrappedRoute page={GroupsPage} component={GroupsDiscover} />}
    />
    <Route
      path="/groups/suggested"
      element={
        <WrappedRoute page={GroupsPendingPage} component={GroupsSuggested} />
      }
    />
    <Route
      path="/groups/discover/tags/:id"
      element={<WrappedRoute page={GroupsPendingPage} component={GroupsTag} />}
    />
    <Route
      path="/groups/discover/category/:id"
      element={
        <WrappedRoute page={GroupsPendingPage} component={GroupsCategory} />
      }
    />
    <Route
      path="/groups/pending-requests"
      element={
        <WrappedRoute
          page={GroupsPendingPage}
          component={PendingGroupRequests}
        />
      }
    />
    <Route path="/group/:groupSlug" element={<GroupGuard />}>
      <Route
        index
        element={
          <WrappedRoute
            publicRoute
            page={GroupPage}
            component={GroupTimeline}
          />
        }
      />
      <Route
        path="media"
        element={
          <WrappedRoute
            publicRoute={!authenticatedProfile}
            page={GroupPage}
            component={GroupGallery}
          />
        }
      />
      <Route
        path="about"
        element={
          <WrappedRoute
            publicRoute={!authenticatedProfile}
            page={GroupPage}
            component={GroupAbout}
          />
        }
      />
      <Route
        path="about/pending-requests"
        element={
          <WrappedRoute
            publicRoute={!authenticatedProfile}
            page={GroupPage}
            component={GroupAbout}
          />
        }
      />
      <Route
        path="tags"
        element={<WrappedRoute page={GroupPage} component={GroupTags} />}
      />
      <Route
        path="tag/:tagId"
        element={
          <WrappedRoute page={GroupsPendingPage} component={GroupTagTimeline} />
        }
      />
      <Route
        path="search"
        element={
          <WrappedRoute page={GroupSearchPage} component={GroupSearch} />
        }
      />
      <Route
        path="events"
        element={<WrappedRoute page={GroupPage} component={GroupEvents} />}
      />
    </Route>
    {/* These routes doesn't require GroupGuard and waiting period for useGroupLookup to be fetched */}
    <Route
      path="/group/:groupSlug/statuses/:statusId"
      element={<LegacyGroupStatusRedirect />}
    />
    <Route
      path="/group/:groupSlug/posts/:statusId"
      element={
        <WrappedRoute publicRoute page={StatusPage} component={Status} />
      }
    />
    <Route
      path="/users/:username/statuses/:statusId"
      element={<LegacyUserStatusRedirect />}
    />

    <Route
      path="/statuses/new"
      element={<WrappedRoute page={DefaultPage} component={NewStatus} />}
    />
    <Route
      path="/statuses/:statusId"
      element={<WrappedRoute page={StatusPage} component={Status} />}
    />

    {features.predictions && (
      <>
        <Route
          path="/predictions"
          element={
            <WrappedRoute page={PredictionsPage} component={Predictions} />
          }
        />
        <Route
          path="/predictions/:id"
          element={
            <WrappedRoute page={PredictionPage} component={PredictionDetail} />
          }
        />
      </>
    )}

    <Route
      path="/settings/preferences"
      element={
        <WrappedRoute page={SettingsPage} component={PreferencesSettings} />
      }
    />
    <Route
      path="/settings/privacy"
      element={<WrappedRoute page={SettingsPage} component={PrivacySettings} />}
    />
    <Route
      path="/settings/security"
      element={
        <WrappedRoute page={SettingsPage} component={SecuritySettings} />
      }
    />
    <Route
      path="/settings/subscriptions"
      element={
        <WrappedRoute page={SettingsPage} component={SubscriptionSettings} />
      }
    />
    <Route
      path="/settings/subscriptions/payments"
      element={
        <WrappedRoute page={FullPage} component={SubscriptionPayments} />
      }
    />
    {features.boosts && (
      <>
        <Route
          path="/settings/boosts"
          element={<WrappedRoute page={DefaultPage} component={BoostDashboard} />}
        />
        <Route
          path="/settings/boosts/:boostId"
          element={<WrappedRoute page={DefaultPage} component={BoostDashboard} />}
        />
      </>
    )}
    <Route
      path="/settings/messages"
      element={
        <WrappedRoute page={SettingsPage} component={MessagesSettings} />
      }
    />
    <Route
      path="/settings/profile"
      element={<WrappedRoute page={SettingsPage} component={ProfileSettings} />}
    />
    <Route
      path="/settings/other"
      element={<WrappedRoute page={SettingsPage} component={OtherSettings} />}
    />
    <Route
      path="/settings/blocks"
      element={<WrappedRoute page={SettingsPage} component={BlocksSettings} />}
    />
    <Route
      path="/settings/download"
      element={
        <WrappedRoute
          page={SettingsPage}
          component={DownloadYourInformationSettings}
        />
      }
    />
    <Route
      path="/settings/mutes"
      element={<WrappedRoute page={SettingsPage} component={MuteSettings} />}
    />
    <Route
      path="/settings/discoverability"
      element={
        <WrappedRoute page={SettingsPage} component={DiscoverabilitySettings} />
      }
    />
    <Route
      path="/settings/password"
      element={
        <WrappedRoute page={SettingsPage} component={PasswordSettings} />
      }
    />
    <Route
      path="/settings/mfa"
      element={<WrappedRoute page={SettingsPage} component={MfaSettings} />}
    />
    <Route
      path="/settings/ads"
      element={<WrappedRoute page={SettingsPage} component={AdsSettings} />}
    />
    <Route
      path="/settings/account"
      element={
        <WrappedRoute page={SettingsPage} component={DeleteAccountSettings} />
      }
    />
    <Route
      path="/settings/sessions"
      element={
        <WrappedRoute page={SettingsPage} component={SessionsSettings} />
      }
    />
    <Route
      path="/settings"
      element={<WrappedRoute page={SettingsPage} component={Settings} />}
    />

    <Route
      path="/info"
      element={
        <WrappedRoute publicRoute page={DefaultPage} component={ServerInfo} />
      }
    />

    <Route
      path="/share"
      element={<WrappedRoute page={DefaultPage} component={Share} />}
    />

    {features.bookmarks && (
      <Route
        path="/bookmarks"
        element={<WrappedRoute page={DefaultPage} component={Bookmarks} />}
      />
    )}

    {/* 404 catch-all */}
    <Route
      path="*"
      element={
        <WrappedRoute
          publicRoute
          page={DefaultPage}
          component={GenericNotFound}
        />
      }
    />
  </Route>
);
